import React from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AssessmentIcon from '@mui/icons-material/Assessment';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import PersonIcon from '@mui/icons-material/Person';
import { BreakdownPanel, MetricPanel, StatTile } from './metricsPanels';

/**
 * ClientMetricsDialog — the Clients page's secondary metrics (Level of Care census, Housing,
 * Caseload by Counselor) in one dialog of panels, opened from the "View metrics" header button
 * instead of a collapsible band under the header.
 *
 * Props:
 *   open, onClose
 *   levelOfCareTotals   — [[label, value]] already grouped/ordered by the page
 *   getLevelOfCareIcon  — (label) => MUI icon component
 *   housingData / housingColors, counselorData / counselorColors — [{ label, value }] + { [label]: hex }
 *   isLoading           — renders skeletons while the first page of clients is loading
 */
export const ClientMetricsDialog = ({
  open,
  onClose,
  levelOfCareTotals = [],
  getLevelOfCareIcon = () => AssessmentIcon,
  housingData = [],
  housingColors = {},
  counselorData = [],
  counselorColors = {},
  isLoading = false,
}) => {
  const levelOfCareTotal = levelOfCareTotals.reduce((sum, [, value]) => sum + (value || 0), 0);

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6, pb: 1 }}>
        <Typography component="div" sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>Client Metrics</Typography>
        <Typography component="div" sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25 }}>
          Totals across the current client filter
        </Typography>
        <IconButton aria-label="close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: '#F7F9FC', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <MetricPanel
          icon={AssessmentIcon}
          title="Level of Care"
          subtitle={!isLoading && levelOfCareTotal > 0 ? `${levelOfCareTotal} assigned` : undefined}
        >
          {!isLoading && levelOfCareTotals.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: 'text.secondary', py: 2, textAlign: 'center' }}>
              No clients have a level of care assigned.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
              {(isLoading && levelOfCareTotals.length === 0
                ? [['3.1 Residential', 0], ['IOP', 0], ['PHP', 0], ['TOP', 0]]
                : levelOfCareTotals
              ).map(([label, value]) => (
                <StatTile
                  key={label}
                  label={label}
                  value={value}
                  caption={`${levelOfCareTotal > 0 ? Math.round((value / levelOfCareTotal) * 100) : 0}% of clients`}
                  icon={getLevelOfCareIcon(label)}
                  isLoading={isLoading}
                />
              ))}
            </Box>
          )}
        </MetricPanel>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <BreakdownPanel icon={HomeWorkOutlinedIcon} title="Housing" data={housingData} colorMap={housingColors} isLoading={isLoading} />
          <BreakdownPanel icon={PersonIcon} title="Caseload by Counselor" data={counselorData} colorMap={counselorColors} isLoading={isLoading} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" color="primary">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientMetricsDialog;
